import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowRight, CheckCircle2, RefreshCcw } from "@/components/icons";
import { useStore } from "@/store/store";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { getOnboardingImportSnapshot, ONBOARDING_IMPORT_TASKS } from "@/lib/onboarding";
import { useI18n } from "@/i18n";

export function OnboardingStatusBar() {
  const { state, actions } = useStore();
  const { t } = useI18n();
  const nav = useNavigate();
  const onboarding = state.onboarding;
  const [now, setNow] = useState(Date.now());
  const snapshot = getOnboardingImportSnapshot(onboarding, now);

  useEffect(() => {
    if (!onboarding.importStartedAt || onboarding.businessDataReadyAt) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(id);
  }, [onboarding.importStartedAt, onboarding.businessDataReadyAt]);

  useEffect(() => {
    if (snapshot.ready && !onboarding.businessDataReadyAt) actions.finishOnboardingImport();
  }, [actions, snapshot.ready, onboarding.businessDataReadyAt]);

  if (onboarding.reviewedAt) return null;

  if (!onboarding.importStartedAt) {
    if (onboarding.status === "not_started") return null;
    return (
      <div className="border-b border-warning/40 bg-warning-soft/65 px-4 py-3 lg:px-6">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-warning/35 bg-surface text-warning-soft-foreground">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-foreground">{t("onboarding.status.finishSetup")}</p>
            <p className="truncate text-[11px] text-muted-foreground">{t("onboarding.status.finishSetupDescription")}</p>
          </div>
          <Button size="sm" className="shrink-0" onClick={() => nav("/onboarding")}>{t("onboarding.status.continueSetup")}</Button>
        </div>
      </div>
    );
  }

  if (snapshot.ready) {
    return (
      <div className="border-b border-success/30 bg-success-soft/80 px-4 py-2.5 lg:px-6" aria-live="polite">
        <div className="mx-auto flex max-w-7xl items-center gap-3">
          <CheckCircle2 className="h-5 w-5 shrink-0 text-success" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-foreground">{t("onboarding.status.businessDataReady")}</p>
            <p className="truncate text-[11px] text-muted-foreground">{t("onboarding.status.readyDescription")}</p>
          </div>
          <Button size="sm" variant="outline" className="shrink-0 bg-surface" onClick={() => {
            nav("/settings/channels?review=imported");
          }}>{t("onboarding.status.reviewData")} <ArrowRight className="h-4 w-4" /></Button>
        </div>
      </div>
    );
  }

  const label = snapshot.activeTask?.requires === "website"
    ? t("onboarding.status.waitingWebsite")
    : snapshot.activeTask?.requires === "channel"
      ? t("onboarding.status.waitingChannel")
      : snapshot.activeTask?.activeKey
        ? t(snapshot.activeTask.activeKey)
        : t("onboarding.status.preparingWorkspace");

  return (
    <div className="border-b border-warning/35 bg-warning-soft/45 px-4 py-2.5 lg:px-6" aria-live="polite">
      <div className="mx-auto flex max-w-7xl items-center gap-3">
        <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-warning/30 bg-surface text-warning-soft-foreground">
          <RefreshCcw className="h-4 w-4" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-3">
            <p className="truncate text-xs font-semibold text-foreground">{t("onboarding.status.iliaPreparing")}</p>
            <p className="hidden shrink-0 text-[11px] font-medium text-primary sm:block">{t("onboarding.status.taskProgress", { count: snapshot.completedCount, total: ONBOARDING_IMPORT_TASKS.length })}</p>
          </div>
          <div className="mt-1 flex items-center gap-3">
            <Progress value={snapshot.progress} className="h-1 max-w-36 bg-primary/15 sm:max-w-52" />
            <p className="truncate text-[11px] text-muted-foreground">{label}</p>
          </div>
        </div>
        <Button size="sm" variant="outline" className="shrink-0 bg-surface" onClick={() => nav("/onboarding")}>{t("onboarding.status.viewSetup")}</Button>
      </div>
    </div>
  );
}
